import { DateTime } from 'luxon'
import { makeAutoObservable, runInAction } from 'mobx'
import { bewizrAPI, initBeWizrAPI } from '~/apis/bewizr'
import config from '~/config'
import { BeWizrProfile } from '~/models'
import { HrefResolver } from '~/navigation'
import appStore from './appStore'
import authenticationStore from './authenticationStore'
import profileStore from './profileStore'
import { register } from './support'

export class BeWizrStore {

  constructor() {
    makeAutoObservable(this)
  }

  public authenticated: boolean = false

  public profile: BeWizrProfile | null = null

  public resolveHref = (match: RegExpMatchArray): string => {
    const href    = `${config.urls.wizr}/${match[1]}`
    const tokens  = authenticationStore.getOAuthTokens('bewizr')
    if (tokens == null) {
      console.warn(`No access token available to resolve BeWizr href: "${href}"`)
      return href
    }

    const resolved = new URL(`${config.urls.wizr}/login_and_redirect`)
    resolved.searchParams.set('next', href)
    resolved.searchParams.set('access_token', tokens.access_token)
    resolved.searchParams.set('return_to', window.location.href)
    if (profileStore.participant != null) {
      resolved.searchParams.set('language', profileStore.participant?.language)
    }

    return resolved.href
  }

  public init() {
    authenticationStore.on('oauth:token-refreshed', this.onLogIn)
    authenticationStore.on('login', this.onLogIn)
    authenticationStore.on('logout', this.onLogOut)
    if (authenticationStore.loginStatus === 'logged-in') {
      this.onLogIn()
    }
  }

  private onLogIn = () => {
    if (appStore.variant !== 'full') { return }

    const tokens = authenticationStore.getOAuthTokens('bewizr')
    if (tokens == null) { return }

    // If the current token is expired, don't initialize the API to prevent an authentication error
    // The authentication store will refresh the token, calling this event handler once again
    const now = DateTime.now()
    if (tokens.expires_at <= now.toSeconds()) { return }

    initBeWizrAPI(tokens.access_token)

    HrefResolver.register(
      new RegExp(`^${config.urls.wizr}/(.*)`, 'i'),
      this.resolveHref,
    )

    this.fetchProfile()

    this.authenticated = true
  }

  public fetchProfile = async () => {
    const api = bewizrAPI()
    if (api == null) { return null }

    const response = await api.get('profile/')
    if (response.status !== 200) { return response }

    runInAction(() => {
      this.profile = BeWizrProfile.deserialize(response.data)
    })

  }

  public switchLanguage = async (language: string) => {
    const api = bewizrAPI()
    if (api == null) { return true }

    const response = await api.post('set_language/', {language})
    if (response.status !== 200) { return response }

    return true
  }

  private onLogOut = () => {
    this.authenticated = false
  }
}

export default register(new BeWizrStore())